import React, { lazy, Suspense } from "react";

// Auto-reload once when a lazy chunk fails to load (typically a stale hash after deploy)
const lazyWithRetry = <T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) =>
  lazy(async () => {
    const KEY = "lumza:chunk-reload-ts";
    const COOLDOWN_MS = 10_000; // allow another reload after 10s (handles future deploys)
    try {
      const mod = await factory();
      // Successful load — clear the marker so future stale-chunk errors can trigger a reload again
      if (typeof window !== "undefined") sessionStorage.removeItem(KEY);
      return mod;
    } catch (err: any) {
      const msg = String(err?.message || err);
      const isChunkError =
        /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|error loading dynamically imported module/i.test(
          msg
        );
      if (isChunkError && typeof window !== "undefined") {
        const last = Number(sessionStorage.getItem(KEY) || 0);
        const now = Date.now();
        if (!last || now - last > COOLDOWN_MS) {
          sessionStorage.setItem(KEY, String(now));
          window.location.reload();
          // Never-resolving promise so Suspense fallback stays visible until reload
          return new Promise(() => {}) as Promise<{ default: T }>;
        }
      }
      throw err;
    }
  });
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { SearchProvider } from "@/contexts/SearchContext";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { PremiumProvider } from "@/contexts/PremiumContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";


// Eagerly load homepage
import Index from "./pages/Index";

// Lazy load everything else
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const InstallApp = lazyWithRetry(() => import("./pages/InstallApp"));
const SearchResults = lazyWithRetry(() => import("./pages/SearchResults"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const LegalHub = lazyWithRetry(() => import("./pages/LegalHub"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const MerchantTerms = lazyWithRetry(() => import("./pages/MerchantTerms"));
const POPIACompliance = lazyWithRetry(() => import("./pages/POPIACompliance"));
const RefundPolicy = lazyWithRetry(() => import("./pages/RefundPolicy"));
const AcceptableUsePolicy = lazyWithRetry(() => import("./pages/AcceptableUsePolicy"));
const HelpCenter = lazyWithRetry(() => import("./pages/HelpCenter"));
const CustomerHelp = lazyWithRetry(() => import("./pages/help/CustomerHelp"));
const DriverHelp = lazyWithRetry(() => import("./pages/help/DriverHelp"));
const MerchantHelp = lazyWithRetry(() => import("./pages/help/MerchantHelp"));

// Customer Pages
const StorePage = lazyWithRetry(() => import("./pages/customer/RestaurantPage"));
const CheckoutPage = lazyWithRetry(() => import("./pages/customer/CheckoutPage"));
const OrderTrackingPage = lazyWithRetry(() => import("./pages/customer/OrderTrackingPage"));
const BrowsePage = lazyWithRetry(() => import("./pages/customer/BrowsePage"));
const FavoritesPage = lazyWithRetry(() => import("./pages/customer/FavoritesPage"));
const OrdersPage = lazyWithRetry(() => import("./pages/customer/OrdersPage"));
const SettingsPage = lazyWithRetry(() => import("./pages/customer/SettingsPage"));

// Vendor Pages
const VendorDashboard = lazyWithRetry(() => import("./pages/vendor/VendorDashboard"));
const VendorOrders = lazyWithRetry(() => import("./pages/vendor/VendorOrders"));
const VendorAnalytics = lazyWithRetry(() => import("./pages/vendor/VendorAnalytics"));
const VendorMenu = lazyWithRetry(() => import("./pages/vendor/VendorMenu"));
const VendorSettings = lazyWithRetry(() => import("./pages/vendor/VendorSettings"));

// Driver Pages
const DriverDashboard = lazyWithRetry(() => import("./pages/driver/DriverDashboard"));
const DriverEarnings = lazyWithRetry(() => import("./pages/driver/DriverEarnings"));
const DriverDeliveries = lazyWithRetry(() => import("./pages/driver/DriverDeliveries"));
const DriverAvailableOrders = lazyWithRetry(() => import("./pages/driver/DriverAvailableOrders"));

// Admin Pages
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/AdminDashboard"));

// Onboarding Pages
const VendorOnboarding = lazyWithRetry(() => import("./pages/onboarding/VendorOnboarding"));
const DriverOnboarding = lazyWithRetry(() => import("./pages/onboarding/DriverOnboarding"));

// Other Pages
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const About = lazyWithRetry(() => import("./pages/About"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <PremiumProvider>
        <LanguageProvider>
          <SearchProvider>
            <CartProvider>
              <BrowserRouter>
              <TooltipProvider>
                <div className="min-h-screen bg-background text-foreground">
                  <Toaster />
                  <Sonner />
                  <ScrollToTop />
                  <Suspense fallback={<div className="min-h-screen bg-background" />}>
                  <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/legal" element={<LegalHub />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/merchant-terms" element={<MerchantTerms />} />
                  <Route path="/popia-compliance" element={<POPIACompliance />} />
                  <Route path="/popia" element={<POPIACompliance />} />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route path="/acceptable-use" element={<AcceptableUsePolicy />} />
                  <Route path="/help" element={<HelpCenter />} />
                  <Route path="/help/customers" element={<CustomerHelp />} />
                  <Route path="/help/drivers" element={<DriverHelp />} />
                  <Route path="/help/merchants" element={<MerchantHelp />} />
                  <Route path="/install" element={<InstallApp />} />
                  <Route path="/search" element={<SearchResults />} />
                  
                  {/* Onboarding Routes */}
                  <Route path="/onboard/vendor" element={
                    <ProtectedRoute>
                      <VendorOnboarding />
                    </ProtectedRoute>
                  } />
                  <Route path="/onboard/driver" element={
                    <ProtectedRoute>
                      <DriverOnboarding />
                    </ProtectedRoute>
                  } />
                  
                  {/* Customer Routes */}
                  <Route path="/browse" element={<BrowsePage />} />
                  <Route path="/favorites" element={<FavoritesPage />} />
                  <Route path="/orders" element={<OrdersPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/store/:id" element={<StorePage />} />
                  <Route path="/restaurant/:id" element={<StorePage />} />
                  <Route path="/checkout" element={
                    <ProtectedRoute>
                      <CheckoutPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/order-tracking/:orderId" element={<OrderTrackingPage />} />
                  
                  {/* Vendor Routes - Protected */}
                  <Route path="/vendor" element={
                    <ProtectedRoute requiredRole="vendor">
                      <VendorDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/vendor/dashboard" element={
                    <ProtectedRoute requiredRole="vendor">
                      <VendorDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/vendor/orders" element={
                    <ProtectedRoute requiredRole="vendor">
                      <VendorOrders />
                    </ProtectedRoute>
                  } />
                  <Route path="/vendor/analytics" element={
                    <ProtectedRoute requiredRole="vendor">
                      <VendorAnalytics />
                    </ProtectedRoute>
                  } />
                  <Route path="/vendor/menu" element={
                    <ProtectedRoute requiredRole="vendor">
                      <VendorMenu />
                    </ProtectedRoute>
                  } />
                  <Route path="/vendor/settings" element={
                    <ProtectedRoute requiredRole="vendor">
                      <VendorSettings />
                    </ProtectedRoute>
                  } />
                  
                  {/* Driver Routes - Protected */}
                  <Route path="/driver" element={
                    <ProtectedRoute requiredRole="driver">
                      <DriverDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/driver/dashboard" element={
                    <ProtectedRoute requiredRole="driver">
                      <DriverDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/driver/earnings" element={
                    <ProtectedRoute requiredRole="driver">
                      <DriverEarnings />
                    </ProtectedRoute>
                  } />
                  <Route path="/driver/deliveries" element={
                    <ProtectedRoute requiredRole="driver">
                      <DriverDeliveries />
                    </ProtectedRoute>
                  } />
                  <Route path="/driver/available-orders" element={
                    <ProtectedRoute requiredRole="driver">
                      <DriverAvailableOrders />
                    </ProtectedRoute>
                  } />
                  
                  {/* Admin Routes - Protected */}
                  <Route path="/admin/dashboard" element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminDashboard />
                    </ProtectedRoute>
                  } />
                  
                  {/* Other Routes */}
                  <Route path="/about" element={<About />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  } />
                  
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>
                  
                </div>
              </TooltipProvider>
              </BrowserRouter>
            </CartProvider>
          </SearchProvider>
        </LanguageProvider>
      </PremiumProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
